/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { createSlice, PayloadAction, type ActionReducerMapBuilder } from "@reduxjs/toolkit"
import { selectComputedRanges } from "features/initialScrs/InitialScrsSlice"
import { STEP_SIZES } from "features/results/components/HistoricalDataFilters"
import { submissionCommitted } from "features/userParameters/submissionCommitted"
import {
  HistoricalDataFilters,
  initialHistoricalDataFilters,
} from "services/computations/HistoricalDataFilters"

import type { RootState } from "app/store"
import { DeepReadonly } from "utils/readonly"

// AIDEV-NOTE: Use immutable reducer returns so strict readonly parameter linting does not rely on mutable Draft state.

type YearRangePayload = Readonly<{
  firstStartingYear: number
  lastStartingYear: number
}>

type DrawdownRangePayload = Readonly<{
  minAbsDrawdown: number
  maxAbsDrawdown: number
}>

type CapeRangePayload = Readonly<{
  minCape: number
  maxCape: number
}>

export type InitializeFiltersPayload = Readonly<{
  yearRange: Readonly<{ min: number; max: number }>
  drawdownRange: Readonly<{ min: number; max: number }>
  capeRanges: Readonly<{
    ern: Readonly<{ min: number; max: number }>
    shiller: Readonly<{ min: number; max: number }>
  }>
}>

// Builds the initializeFilters action payload from the initialScrs slice's
// computedRanges, or null when any of the four ranges isn't available yet.
// Shared by Results.tsx's own initializer effect and UserParametersForm.tsx's
// eager initialization on first submission (see the AIDEV-NOTE at that call
// site for why both exist) so the gating conditions can't drift apart.
export const buildInitializeFiltersPayload = (
  computedRanges: ReturnType<typeof selectComputedRanges>
): InitializeFiltersPayload | null => {
  if (
    !computedRanges.years ||
    !computedRanges.absoluteDrawdowns ||
    !computedRanges.capeRanges.ern ||
    !computedRanges.capeRanges.shiller
  ) {
    return null
  }

  return {
    yearRange: computedRanges.years,
    drawdownRange: computedRanges.absoluteDrawdowns,
    capeRanges: {
      ern: computedRanges.capeRanges.ern,
      shiller: computedRanges.capeRanges.shiller,
    },
  }
}

// Helper function to round numbers based on step size
const roundToStep = (value: number, step: number, roundFunction: (x: number) => number): number => {
  return roundFunction(value / step) * step
}

// Round down to step
const roundDownToStep = (value: number, step: number): number => {
  return roundToStep(value, step, Math.floor)
}

// Round up to step
const roundUpToStep = (value: number, step: number): number => {
  return roundToStep(value, step, Math.ceil)
}

// Add flag to track if filters are initialized from data
interface HistoricalDataFiltersState {
  filters: HistoricalDataFilters
  initialized: boolean
}

const initialState: HistoricalDataFiltersState = {
  filters: initialHistoricalDataFilters,
  initialized: false,
}

// The filters a set of data ranges initialises, rounded outward to the sliders' steps so the
// initial selection covers every row. Exported because submitForm computes a first
// submission's results against exactly these filters before anything is stored, and the
// submissionCommitted case below then stores the same value.
export const applyFilterInitialization = (
  state: DeepReadonly<HistoricalDataFiltersState>,
  payload: InitializeFiltersPayload
): HistoricalDataFiltersState => {
  const firstStartingYear = Math.floor(payload.yearRange.min)
  const lastStartingYear = Math.ceil(payload.yearRange.max)
  const minAbsDrawdown = roundDownToStep(payload.drawdownRange.min, STEP_SIZES.DRAWDOWN)
  const maxAbsDrawdown = roundUpToStep(payload.drawdownRange.max, STEP_SIZES.DRAWDOWN)

  // The CAPE bounds follow whichever model is selected, rounded to whole steps.
  const capeRange =
    state.filters.cape === "ern" ? payload.capeRanges.ern : payload.capeRanges.shiller

  return {
    ...state,
    initialized: true,
    filters: {
      ...state.filters,
      firstStartingYear,
      lastStartingYear,
      minAbsDrawdown,
      maxAbsDrawdown,
      minCape: roundDownToStep(capeRange.min, STEP_SIZES.CAPE),
      maxCape: roundUpToStep(capeRange.max, STEP_SIZES.CAPE),
    },
  }
}

export const historicalDataFiltersSlice = createSlice({
  name: "historicalDataFilters",
  initialState,
  reducers: {
    setCapeModel: (
      state: DeepReadonly<HistoricalDataFiltersState>,
      action: DeepReadonly<PayloadAction<"shiller" | "ern">>
    ): HistoricalDataFiltersState => ({
      ...state,
      filters: {
        ...state.filters,
        cape: action.payload,
      },
    }),
    setYearRange: (
      state: DeepReadonly<HistoricalDataFiltersState>,
      action: DeepReadonly<PayloadAction<YearRangePayload>>
    ): HistoricalDataFiltersState => {
      // Round year values to nearest integer (step = 1)
      return {
        ...state,
        filters: {
          ...state.filters,
          firstStartingYear: Math.floor(action.payload.firstStartingYear),
          lastStartingYear: Math.ceil(action.payload.lastStartingYear),
        },
      }
    },
    setDrawdownRange: (
      state: DeepReadonly<HistoricalDataFiltersState>,
      action: DeepReadonly<PayloadAction<DrawdownRangePayload>>
    ): HistoricalDataFiltersState => {
      // Preserve precise values for drawdown (similar to CAPE)
      return {
        ...state,
        filters: {
          ...state.filters,
          minAbsDrawdown: action.payload.minAbsDrawdown,
          maxAbsDrawdown: action.payload.maxAbsDrawdown,
        },
      }
    },
    setCapeRange: (
      state: DeepReadonly<HistoricalDataFiltersState>,
      action: DeepReadonly<PayloadAction<CapeRangePayload>>
    ): HistoricalDataFiltersState => {
      // No rounding for slider movements - keep precise values
      return {
        ...state,
        filters: {
          ...state.filters,
          minCape: action.payload.minCape,
          maxCape: action.payload.maxCape,
        },
      }
    },
    initializeFilters: (
      state: DeepReadonly<HistoricalDataFiltersState>,
      action: DeepReadonly<PayloadAction<InitializeFiltersPayload>>
    ): HistoricalDataFiltersState => applyFilterInitialization(state, action.payload),
    setHistoricalDataFilters: (
      state: DeepReadonly<HistoricalDataFiltersState>,
      action: DeepReadonly<PayloadAction<DeepReadonly<HistoricalDataFilters>>>
    ): HistoricalDataFiltersState => ({
      ...state,
      filters: {
        ...action.payload,
      },
      initialized: true,
    }),
    resetInitialized: (
      state: DeepReadonly<HistoricalDataFiltersState>
    ): HistoricalDataFiltersState => ({
      ...state,
      initialized: false,
    }),
  },
  extraReducers: (builder: Readonly<ActionReducerMapBuilder<HistoricalDataFiltersState>>) => {
    builder.addCase(
      submissionCommitted,
      (
        state: DeepReadonly<HistoricalDataFiltersState>,
        action: DeepReadonly<ReturnType<typeof submissionCommitted>>
      ): DeepReadonly<HistoricalDataFiltersState> =>
        // AIDEV-NOTE: (#1125) A submission that does not initialise the filters must hand back
        // this same state object, not an equal copy. Results.tsx selects `filters` by
        // reference and useCoalescedRecalculation's observing effect depends on it, so a new
        // object would re-run that effect and feed the recalculation scheduler an observation
        // that no filter change produced, on every submission after the first.
        action.payload.initializeFilters === null
          ? state
          : applyFilterInitialization(state, action.payload.initializeFilters)
    )
  },
})

export const {
  setCapeModel,
  setYearRange,
  setDrawdownRange,
  setCapeRange,
  setHistoricalDataFilters,
  initializeFilters,
  resetInitialized,
} = historicalDataFiltersSlice.actions

export const selectHistoricalDataFilters = (state: DeepReadonly<RootState>) =>
  state.historicalDataFilters.filters
export const selectFiltersInitialized = (state: DeepReadonly<RootState>) =>
  state.historicalDataFilters.initialized

export default historicalDataFiltersSlice.reducer
